/**
 * @fileoverview
 * Error Utility Functions
 * Helper functions for extracting error messages from standardized API errors
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorResponse, isErrorResponse } from '../interfaces/api-response.interface';

/**
 * Extract error message from HttpErrorResponse
 * Handles both standardized and legacy error formats
 */
export function getErrorMessage(error: HttpErrorResponse | any, defaultMessage: string = 'An error occurred'): string {
  // Check if error interceptor has already extracted errorMessage
  if (error.errorMessage) {
    return error.errorMessage;
  }

  // Check if error follows standardized format
  if (error.error && isErrorResponse(error.error)) {
    const apiError = error.error as ApiErrorResponse;
    return apiError.message || defaultMessage;
  }

  // Fallback to legacy format
  if (error.error?.message) {
    return Array.isArray(error.error.message)
      ? error.error.message.join(', ')
      : error.error.message;
  }

  // Use error message if available
  if (error.message) {
    return error.message;
  }

  return defaultMessage;
}

/**
 * Extract error code from HttpErrorResponse
 */
export function getErrorCode(error: HttpErrorResponse | any): string | undefined {
  // Check if error interceptor has already extracted errorCode
  if (error.errorCode) {
    return error.errorCode;
  }

  // Check if error follows standardized format
  if (error.error && isErrorResponse(error.error)) {
    const apiError = error.error as ApiErrorResponse;
    return apiError.error?.code;
  }

  return undefined;
}

/**
 * Extract error details from HttpErrorResponse
 */
export function getErrorDetails(error: HttpErrorResponse | any): any {
  // Check if error interceptor has already extracted errorDetails
  if (error.errorDetails) {
    return error.errorDetails;
  }

  // Check if error follows standardized format
  if (error.error && isErrorResponse(error.error)) {
    const apiError = error.error as ApiErrorResponse;
    return apiError.error?.details;
  }

  return undefined;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: HttpErrorResponse | any): boolean {
  return error.status === 422 || getErrorCode(error) === 'VALIDATION_ERROR';
}

/**
 * Extract validation errors as a record of field -> messages
 */
export function getValidationErrors(error: HttpErrorResponse | any): Record<string, string[]> | null {
  if (!isValidationError(error)) {
    return null;
  }

  const details = getErrorDetails(error);
  if (details && typeof details === 'object') {
    // Convert to Record<string, string[]>
    const validationErrors: Record<string, string[]> = {};
    Object.keys(details).forEach(field => {
      const messages = details[field];
      validationErrors[field] = Array.isArray(messages) ? messages : [String(messages)];
    });
    return validationErrors;
  }

  return null;
}
